'use client';

import { create } from 'zustand';
import type { VersionTree, VersionTreeRow } from '@/versions/versionTree';

interface VersionTreeState {
	scope: { workspace: string; agent: string; agentId: number } | null;
	rows: VersionTreeRow[];
	selectedVersionId: number | null;
	liveVersionId: number | null;
	role: string | null;
	tree: VersionTree | null;
	loadingTree: boolean;
	/** The agent whose rows and selected tree have finished loading at least once. */
	loadedAgentId: number | null;
	load: (scope: { workspace: string; agent: string; agentId: number }) => Promise<void>;
	refreshRows: () => Promise<void>;
	loadTree: (versionId: number) => Promise<void>;
	select: (versionId: number) => Promise<void>;
	/** Shows a version the server already selected for this user, such as a draft the assistant forked. */
	follow: (versionId: number) => Promise<void>;
	/**
	 * After a server write into `versionId`: re-reads the rows, and the tree when that version is
	 * still the user's selection. Unlike `follow`, reloads a tree already shown.
	 */
	reloadWritten: (versionId: number) => Promise<void>;
	/**
	 * After a Realtime change to the agent's rows: re-reads the rows once the burst settles (a publish
	 * touches several rows), then reloads the tree when the selection moved or the shown row changed status.
	 */
	syncFromRealtime: () => void;
	/** Keeps the store's copy of a bound draft's files current, for lists outside the editor. */
	setTreeFiles: (versionId: number, files: Record<string, string>) => void;
	/** Two versions shown side by side in the Code panel; `otherId` stays null until the second is picked. */
	compare: { baseId: number; otherId: number | null } | null;
	setCompare: (compare: { baseId: number; otherId: number | null } | null) => void;
}

const REALTIME_SETTLE_MS = 300;
let realtimeTimer: ReturnType<typeof setTimeout> | null = null;

const base = (s: VersionTreeState['scope']) => `/api/${s!.workspace}/${s!.agent}/versions`;

/** The agent's versions and the selected version's tree, shared by the editor and the sidebar. */
export const useVersionTree = create<VersionTreeState>((set, get) => ({
	scope: null,
	rows: [],
	selectedVersionId: null,
	liveVersionId: null,
	role: null,
	tree: null,
	loadingTree: false,
	loadedAgentId: null,
	compare: null,

	load: async (scope) => {
		const prev = get().scope;
		if (prev?.workspace !== scope.workspace || prev?.agent !== scope.agent) {
			set({
				scope,
				rows: [],
				tree: null,
				selectedVersionId: null,
				liveVersionId: null,
				loadedAgentId: null,
				compare: null,
			});
		}
		try {
			await get().refreshRows();
			const id = get().selectedVersionId;
			if (id !== null && get().tree?.id !== id) await get().loadTree(id);
		} finally {
			if (get().scope?.agentId === scope.agentId) set({ loadedAgentId: scope.agentId });
		}
	},

	refreshRows: async () => {
		const scope = get().scope;
		if (!scope) return;
		const res = await fetch(`${base(scope)}/menu`);
		if (!res.ok) return;
		const body = await res.json();
		set({ rows: body.rows, selectedVersionId: body.selectedVersionId, liveVersionId: body.liveVersionId, role: body.role });
	},

	loadTree: async (versionId) => {
		const scope = get().scope;
		if (!scope) return;
		set({ loadingTree: true });
		try {
			const res = await fetch(`${base(scope)}/${versionId}/tree`);
			if (res.ok && get().scope === scope) set({ tree: (await res.json()).tree });
		} finally {
			set({ loadingTree: false });
		}
	},

	select: async (versionId) => {
		const scope = get().scope;
		if (!scope) return;
		const res = await fetch('/api/user/profile/agent-version-preferences', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ agentId: scope.agentId, versionId }),
		});
		if (!res.ok) throw new Error('Failed to select version');
		set({ selectedVersionId: versionId });
		await get().loadTree(versionId);
	},

	follow: async (versionId) => {
		if (!get().scope || get().tree?.id === versionId) return;
		await get().refreshRows();
		set({ selectedVersionId: versionId });
		await get().loadTree(versionId);
	},

	reloadWritten: async (versionId) => {
		if (!get().scope) return;
		await get().refreshRows();
		const selected = get().selectedVersionId;
		if (selected !== null && (selected === versionId || get().tree?.id !== selected)) await get().loadTree(selected);
	},

	syncFromRealtime: () => {
		if (!get().scope) return;
		if (realtimeTimer) clearTimeout(realtimeTimer);
		realtimeTimer = setTimeout(async () => {
			realtimeTimer = null;
			const scope = get().scope;
			if (!scope) return;
			await get().refreshRows();
			if (get().scope !== scope) return;
			const { selectedVersionId: selected, tree, rows } = get();
			if (selected === null) return;
			const row = rows.find((r) => r.id === selected);
			const shownRowChanged = row && tree && (row.status !== tree.status || row.isLive !== tree.isLive);
			if (tree?.id !== selected || shownRowChanged) await get().loadTree(selected);
		}, REALTIME_SETTLE_MS);
	},

	setTreeFiles: (versionId, files) => {
		const tree = get().tree;
		if (tree?.id === versionId) set({ tree: { ...tree, files } });
	},

	setCompare: (compare) => set({ compare }),
}));
